import { useEffect, useState } from 'react';
import { Group, Select, Stack, Table, Text, Title } from '@mantine/core';
import { useSearchParams } from 'react-router';
import {
  GOREV_SUZGEC_VARSAYILANI,
  etkinGorevSuzgeci,
  useGorevSecenekleri,
  useGorevler,
  type GorevSuzgeci,
} from '../api/queries';
import { donemGoster, tarihGoster } from '../lib/format';
import { QueryStatus } from './parts';
import { GorevRozeti } from './gorev-parts';
import { SatirEylemleri, Sayfalama, SuzgecGostergesi } from '../sozlesme';

/**
 * PERİYODİK GÖREV LİSTESİ (F4·S5·B2 — T8; Kapsam §22.6a + §20.3b).
 *
 * **NESNESİNİN MODÜLÜNDE DURUR:** ekipman görevleri *İş ekipmanları*
 * sekmesindedir — menüde satır artmaz ve bağlam yerinde kalır (*vincin
 * görevine bakarken vinç de karşında*). Yükümlülük görevleri kendi modülüne
 * (Yasal uygunluk, S8) gidecek; o modül bugün YOK ve burada TAKLİT EDİLMEZ.
 *
 * ⚠️ **"GÖREVİ KAPAT" DÜĞMESİ YOKTUR ve eklenmez** (§22.6a kilidi): görev
 * **form doldurulunca** kapanır, kapanış kontrol kaydının YAN ETKİSİDİR.
 * Düğme koymak **formsuz kapanış** yolu üretir ve `yapildi` damgasını
 * KANITSIZ bırakır — o damganın tek anlamı *"bu dönemde bir kontrol kaydı
 * doğdu"*dur. Uç da yoktur; düğme yazılsa çağıracağı bir şey bulamaz.
 *
 * ⚠️ **BU BİR IZGARADIR, BİR KUYRUK DEĞİL** (G2-a): kaçırılmış dönemler
 * **geçmiş performans kaydıdır**, bugünün işi değil. Kişiye göre süzülmez —
 * kişisel kuyruk *İşlerim*dedir.
 */
const SUZGEC_ANAHTARI = 'gorev-suzgeci';

export function GorevlerScreen(): React.JSX.Element {
  const [aramaParametreleri] = useSearchParams();
  const [suzgec, setSuzgec] = useState<GorevSuzgeci>(() => baslangicSuzgeci(aramaParametreleri));
  const gorevler = useGorevler(suzgec);
  const secenekler = useGorevSecenekleri();

  // P4 — SÜZGEÇ HATIRLANIR. (Ama GÖRÜNÜR kalır: aşağıdaki gösterge olmadan
  // kullanıcı *"ekipmanlarım kaybolmuş"* der.)
  useEffect(() => {
    sessionStorage.setItem(SUZGEC_ANAHTARI, JSON.stringify(suzgec));
  }, [suzgec]);

  const etkin = etkinGorevSuzgeci(suzgec);
  const veri = gorevler.data;

  return (
    <Stack>
      <Title order={4}>Periyodik görevler</Title>
      {/* ⚠️ **E3 — SEKME KENDİNİ ANLATIR (§P6).** Fark bir ÜRETİM ilişkisidir:
          kural çeşme, görev ondan akan su. ✏️ D2-4 (17.09.2026 — bulgu 81):
          iki paragraf ilişkiyi iki kez anlatıyordu → TEK SATIR (M1: cümle
          gerekli, ama kısa). İki sekmenin cümlesi birbirinin aynasıdır. */}
      <Text size="xs" c="dimmed" data-testid="sekme-iliski-cumlesi">
        Her satır bir <strong>görev kuralından</strong> doğmuş iştir; formu doldurulunca kapanır.
      </Text>
      <Group justify="space-between" align="flex-end">
        <Group gap="sm">
          <Select
            size="xs"
            label="Durum"
            data={[
              { value: 'bekliyor', label: 'Bekleyen' },
              { value: 'yapildi', label: 'Yapılan' },
              { value: 'kacirildi', label: 'Kaçırılan' },
              { value: 'gecersiz', label: 'Geçersiz' },
              { value: 'hepsi', label: 'Hepsi' },
            ]}
            value={suzgec.durum}
            onChange={(deger) =>
              setSuzgec((onceki) => ({
                ...onceki,
                durum: (deger ?? 'bekliyor') as GorevSuzgeci['durum'],
                sayfa: 1,
              }))
            }
            data-testid="gorev-durum-suzgeci"
          />
          <Select
            size="xs"
            label="Ekipman"
            clearable
            searchable
            data={(secenekler.data?.ekipmanlar ?? []).map((e) => ({
              value: e.id,
              label: `${e.ad} — ${e.workplaceAd}`,
            }))}
            value={suzgec.ekipmanId}
            onChange={(deger) => setSuzgec((onceki) => ({ ...onceki, ekipmanId: deger, sayfa: 1 }))}
            data-testid="gorev-ekipman-suzgeci"
          />
        </Group>
        <SuzgecGostergesi
          etkinSayisi={etkin}
          onTemizle={() => setSuzgec(GOREV_SUZGEC_VARSAYILANI)}
        />
      </Group>

      <QueryStatus queries={[gorevler]} />

      {veri && (
        <>
          <Table striped highlightOnHover data-testid="gorev-listesi">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Nesne</Table.Th>
                <Table.Th>Kalem</Table.Th>
                <Table.Th>Dönem</Table.Th>
                <Table.Th>Vade</Table.Th>
                <Table.Th>Durum</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {veri.satirlar.map((satir) => (
                <Table.Tr key={satir.id} data-testid="gorev-satiri">
                  <Table.Td>
                    {satir.nesne.ad}
                    <Text size="xs" c="dimmed">
                      {satir.nesne.workplaceAd}
                    </Text>
                  </Table.Td>
                  <Table.Td>{satir.kalemAd}</Table.Td>
                  <Table.Td>{donemGoster(satir.donem)}</Table.Td>
                  <Table.Td>{tarihGoster(satir.vadeTarihi)}</Table.Td>
                  <Table.Td>
                    <GorevRozeti satir={satir} />
                  </Table.Td>
                  <Table.Td>
                    {/* P2 — TEK BİRİNCİL EYLEM. "Görevi kapat" burada YOK
                        (bkz. dosya başı); detay bir OKUMA sayfasıdır. */}
                    <SatirEylemleri
                      birincil={{
                        etiket: 'Detay',
                        to: `/ekipman/gorevler/${satir.id}`,
                        testId: 'gorev-detay-baglantisi',
                      }}
                    />
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          <Sayfalama
            sayfa={veri.sayfa}
            sayfaBoyutu={veri.sayfaBoyutu}
            toplam={veri.toplam}
            onDegis={(sayfa) => setSuzgec((onceki) => ({ ...onceki, sayfa }))}
          />
        </>
      )}
    </Stack>
  );
}

/**
 * Başlangıç süzgeci: URL parametresi (ekipman satırından gelen bağlam) >
 * hatırlanan süzgeç > varsayılan. Bozuk hatırlama kaydı varsayılana düşer —
 * bozuk ayar davranışı düşürmez (`readTenantSettingBool` sözleşmesi).
 */
function baslangicSuzgeci(parametreler: URLSearchParams): GorevSuzgeci {
  const ekipmanId = parametreler.get('ekipmanId');
  if (ekipmanId) return { ...GOREV_SUZGEC_VARSAYILANI, ekipmanId };
  const kayit = sessionStorage.getItem(SUZGEC_ANAHTARI);
  if (!kayit) return GOREV_SUZGEC_VARSAYILANI;
  try {
    const cozulen = JSON.parse(kayit) as Partial<GorevSuzgeci>;
    return {
      durum: cozulen.durum ?? GOREV_SUZGEC_VARSAYILANI.durum,
      ekipmanId: cozulen.ekipmanId ?? null,
      sayfa: cozulen.sayfa ?? 1,
    };
  } catch {
    return GOREV_SUZGEC_VARSAYILANI;
  }
}
